import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'

import { SalonPageShell } from '@/components/layout/SalonPageShell'
import { StaffAttendancePanel } from '@/components/staff/StaffAttendancePanel'
import { Button } from '@/components/ui/button'
import { api } from '@/lib/api'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { fetchStylistsList } from '@/lib/api/stylists'
import { cn } from '@/lib/utils'

const TABS = [
  { id: 'leave', label: 'Leave' },
  { id: 'attendance', label: 'Attendance' },
  { id: 'schedules', label: 'Schedules' },
]

const inputClass = cn(
  'h-10 w-full rounded-xl border border-border/80 bg-card px-3 text-sm',
  'focus:outline-none focus:ring-2 focus:ring-ring',
)

export function HrPage() {
  const [tab, setTab] = useState('leave')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [requests, setRequests] = useState([])
  const [stylists, setStylists] = useState([])
  const [form, setForm] = useState({
    stylist_id: '',
    starts_on: '',
    ends_on: '',
    reason: '',
  })

  const refreshLeave = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [leaveRes, staff] = await Promise.all([
        api.get('/hr/leave-requests'),
        fetchStylistsList({ is_active: 1 }),
      ])
      setRequests(leaveRes.data?.data?.leave_requests ?? [])
      setStylists(Array.isArray(staff) ? staff : [])
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to load leave requests.'))
      setRequests([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (tab === 'leave') {
      void refreshLeave()
    }
  }, [tab, refreshLeave])

  async function handleCreate(event) {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      await api.post('/hr/leave-requests', {
        stylist_id: Number(form.stylist_id),
        starts_on: form.starts_on,
        ends_on: form.ends_on,
        reason: form.reason || null,
      })
      setForm({ stylist_id: '', starts_on: '', ends_on: '', reason: '' })
      await refreshLeave()
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to submit leave request.'))
    } finally {
      setBusy(false)
    }
  }

  async function handleReview(id, action) {
    setBusy(true)
    setError('')
    try {
      await api.post(`/hr/leave-requests/${id}/${action}`)
      await refreshLeave()
    } catch (err) {
      setError(apiErrorMessage(err, `Unable to ${action} leave request.`))
    } finally {
      setBusy(false)
    }
  }

  return (
    <SalonPageShell title="HR" description="Leave requests, attendance, and schedule notes.">
      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.map((item) => (
          <Button
            key={item.id}
            type="button"
            size="sm"
            variant={tab === item.id ? 'default' : 'outline'}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </Button>
        ))}
      </div>

      {error ? (
        <div className="mb-4 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      {tab === 'leave' ? (
        <div className="max-w-3xl space-y-4">
          <form onSubmit={handleCreate} className="grid gap-3 rounded-2xl border border-border/70 bg-card p-4 sm:grid-cols-2">
            <h2 className="sm:col-span-2 text-lg font-medium">Request leave</h2>
            <select
              className={inputClass}
              required
              value={form.stylist_id}
              onChange={(e) => setForm((current) => ({ ...current, stylist_id: e.target.value }))}
            >
              <option value="">Staff member</option>
              {stylists.map((stylist) => (
                <option key={stylist.id} value={stylist.id}>
                  {stylist.name}
                </option>
              ))}
            </select>
            <input
              className={inputClass}
              type="date"
              required
              value={form.starts_on}
              onChange={(e) => setForm((current) => ({ ...current, starts_on: e.target.value }))}
            />
            <input
              className={inputClass}
              type="date"
              required
              value={form.ends_on}
              onChange={(e) => setForm((current) => ({ ...current, ends_on: e.target.value }))}
            />
            <input
              className={inputClass}
              placeholder="Reason (optional)"
              value={form.reason}
              onChange={(e) => setForm((current) => ({ ...current, reason: e.target.value }))}
            />
            <Button type="submit" disabled={busy} className="w-fit sm:col-span-2">
              Submit request
            </Button>
          </form>

          {loading ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </p>
          ) : (
            <ul className="divide-y divide-border/60 rounded-2xl border border-border/70 bg-card">
              {requests.length === 0 ? (
                <li className="p-4 text-sm text-muted-foreground">No leave requests.</li>
              ) : (
                requests.map((row) => (
                  <li key={row.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                    <div>
                      <p className="font-medium">{row.stylist?.name ?? `Staff #${row.stylist_id}`}</p>
                      <p className="text-xs text-muted-foreground">
                        {row.starts_on} → {row.ends_on}
                        {row.reason ? ` · ${row.reason}` : ''}
                      </p>
                      <p className="mt-1 text-xs capitalize text-muted-foreground">{row.status}</p>
                    </div>
                    {row.status === 'pending' ? (
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          disabled={busy}
                          onClick={() => handleReview(row.id, 'approve')}
                        >
                          Approve
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={busy}
                          onClick={() => handleReview(row.id, 'reject')}
                        >
                          Reject
                        </Button>
                      </div>
                    ) : null}
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      ) : null}

      {tab === 'attendance' ? (
        <div className="max-w-3xl">
          <StaffAttendancePanel />
        </div>
      ) : null}

      {tab === 'schedules' ? (
        <div className="max-w-xl rounded-2xl border border-border/70 bg-card p-4 text-sm text-muted-foreground">
          <p>
            Working hours and salon open days are managed in Settings → Hours. Staff availability for
            booking follows those hours plus leave and blockouts.
          </p>
          <Button asChild variant="outline" className="mt-4">
            <Link to="/settings">Open Settings hours</Link>
          </Button>
        </div>
      ) : null}
    </SalonPageShell>
  )
}
